<template>
  <component :is="embedded ? 'div' : 'v-main'">
    <component :is="embedded ? 'div' : 'v-container'" v-bind="embedded ? {} : { fluid: true }">
      <v-card :elevation="embedded ? 0 : 6" :rounded="embedded ? undefined : 'xxl'">
        <!-- Embedded (dialog) use gets its banner header from AppDialog instead. -->
        <v-card-title v-if="!embedded">
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark> mdi-arrow-left </v-icon> </v-btn
          >Add a Location
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col sm="3">
              <v-select
                v-model="form.country_id"
                :items="countries"
                item-text="description"
                item-value="id"
                outlined
                prepend-inner-icon="mdi-earth"
                :error-messages="errors.country_id"
                @change="onCountryChange"
              >
                <template v-slot:label>Country <span style="color:#f44336;font-weight:bold;">*</span></template>
              </v-select>
            </v-col>
            <v-col sm="3">
              <v-select
                v-model="form.state_id"
                :items="states"
                item-text="description"
                item-value="id"
                outlined
                prepend-inner-icon="mdi-map-marker-radius"
                :error-messages="errors.state_id"
                :disabled="!form.country_id"
                @change="onStateChange"
              >
                <template v-slot:label>State <span style="color:#f44336;font-weight:bold;">*</span></template>
              </v-select>
            </v-col>
            <v-col sm="3">
              <v-select
                v-model="form.city_id"
                :items="cities"
                item-text="description"
                item-value="id"
                outlined
                prepend-inner-icon="mdi-city"
                :error-messages="errors.city_id"
                :disabled="!form.state_id"
              >
                <template v-slot:label>City <span style="color:#f44336;font-weight:bold;">*</span></template>
              </v-select>
            </v-col>
            <v-col sm="3">
              <v-autocomplete
                v-model="form.distributor_id"
                :items="distributors"
                item-text="display_name"
                item-value="id"
                label="Select Distributor"
                outlined
                prepend-inner-icon="mdi-account-tie"
                :error-messages="errors.distributor_id"
                clearable
                :no-data-text="
                  distributors.length === 0
                    ? 'No distributors available'
                    : 'Loading distributors...'
                "
              ></v-autocomplete>
            </v-col>
            <v-col sm="12">
              <v-textarea
                v-model="form.address"
                outlined
                label="Address"
                prepend-inner-icon="mdi-home-map-marker"
                rows="4"
                :error-messages="errors.address"
                @input="form.address = capitalizeWords(form.address)"
              ></v-textarea>
            </v-col>
            <v-col sm="3">
              <v-switch
                class="mt-0 ml-5"
                v-model="form.is_active"
                inset
                :label="`Is Active: ${form.is_active ? 'YES' : 'NO'}`"
              ></v-switch>
            </v-col>
          </v-row>
          <v-divider class="mt-5 mb-5"> </v-divider>
          <v-btn text class="mr-2" @click="embedded ? $emit('close') : $router.back()">Cancel</v-btn>
          <v-btn
            :loading="isLoading"
            :disabled="isLoading"
            rounded
            color="primary"
            dark
            @click="save"
          >
            Save Location
          </v-btn>
        </v-card-text>
      </v-card>

      <!-- Success Dialog - not shown for embedded (dialog) use, which
           closes and lets the parent list refresh instead. -->
      <v-dialog v-if="!embedded" v-model="is_created" width="600">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-check-circle
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Success!</h1>
                      <br />
                      <h3>Location created successfully.</h3>
                      <br />
                      <v-btn color="success" @click="goToList"> OK </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>
    </component>
  </component>
</template>

<script>
import axios from "axios";
export default {
  name: "LocationCreatePage",
  props: {
    // When true, this form is rendered inside another page's dialog (e.g.
    // the Locations list's inline "Add Location") instead of its own
    // routed page - it emits `created`/`close` rather than navigating.
    embedded: { type: Boolean, default: false },
  },
  data() {
    return {
      form: {
        address: "",
        country_id: null,
        state_id: null,
        city_id: null,
        distributor_id: null,
        is_active: true,
      },
      countries: [],
      states: [],
      cities: [],
      distributors: [],
      isLoading: false,
      is_created: false,
    };
  },
  computed: {
    errors() {
      return this.$store.getters["errors/getErrors"] || {};
    },
  },
  mounted() {
    this.$store.dispatch("errors/setErrors", {});
    this.fetchCountries();
    this.fetchDistributors();
  },
  methods: {
    capitalizeWords(value) {
      if (!value) return "";
      return value.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
    },
    async fetchCountries() {
      try {
        const response = await axios.get("/location-countries");
        this.countries = response.data.data || [];
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    },
    async onCountryChange() {
      this.form.state_id = null;
      this.form.city_id = null;
      this.states = [];
      this.cities = [];

      if (this.form.country_id) {
        try {
          const response = await axios.get("/location-states", {
            params: { country_id: this.form.country_id },
          });
          this.states = response.data.data || [];
        } catch (error) {
          console.error("Error fetching states:", error);
        }
      }
    },
    async fetchDistributors() {
      try {
        const response = await axios.get("/users");
        this.distributors = (response.data.data || [])
          .filter((user) => {
            // Filter for distributor position
            return (
              user.position &&
              user.position.name &&
              user.position.name === "DISTRIBUTOR/DEALER"
            );
          })
          .map((user) => ({
            ...user,
            display_name: `${user.first_name} ${user.last_name || ""} (${
              user.phone
            })`,
          }));
      } catch (error) {
        console.error("Error fetching distributors:", error);
      }
    },
    async onStateChange() {
      this.form.city_id = null;
      this.cities = [];

      if (this.form.state_id) {
        try {
          const response = await axios.get("/location-cities", {
            params: { state_id: this.form.state_id },
          });
          this.cities = response.data.data || [];
        } catch (error) {
          console.error("Error fetching cities:", error);
        }
      }
    },
    async save() {
      try {
        this.isLoading = true;
        this.$store.dispatch("errors/setErrors", {});

        // Validation
        let validationErrors = {};
        if (!this.form.country_id) {
          validationErrors.country_id = ["Country is required"];
        }
        if (!this.form.state_id) {
          validationErrors.state_id = ["State is required"];
        }
        if (!this.form.city_id) {
          validationErrors.city_id = ["City is required"];
        }

        if (Object.keys(validationErrors).length > 0) {
          this.$store.dispatch("errors/setErrors", validationErrors);
          this.isLoading = false;
          return;
        }

        await axios.post("/locations", this.form);
        this.isLoading = false;
        if (this.embedded) {
          this.$emit("created");
          this.$emit("close");
        } else {
          this.is_created = true;
        }
      } catch (e) {
        console.log(e);
        this.isLoading = false;
        if (e.response && e.response.data && e.response.data.data) {
          this.$store.dispatch("errors/setErrors", e.response.data.data);
        }
      }
    },
    goToList() {
      this.$router.push("/locations");
    },
  },
};
</script>

<style scoped></style>
